
import { ArtItem, Order, Comment, OrderStatus, User, ArtCategory, ChatThread, ChatMessage, DailyVisit, CommissionTier, SiteSettings } from '../types';
import { INITIAL_ARTS, DEFAULT_CATEGORY_COVERS } from '../constants';
import { storage } from './storageService';

const SITE_URL = window.location.origin;

export const database = {
  resolveMediaUrl(path: string) {
    if (!path || path.trim() === '') return '';
    if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) return path;
    const cleanPath = path.replace(/^\//, '');
    
    // Se o caminho já começa com 'uploads/', não duplicamos
    if (cleanPath.startsWith('uploads/')) {
      return `${SITE_URL}/${cleanPath}`;
    }
    
    return `${SITE_URL}/uploads/${cleanPath}`;
  },

  processArtItem(art: ArtItem): ArtItem {
    const resolvedImage = this.resolveMediaUrl(art.imageUrl);
    const resolvedVideo = art.videoUrl ? this.resolveMediaUrl(art.videoUrl) : '';
    
    return {
      ...art,
      imageUrl: resolvedImage,
      videoUrl: resolvedVideo,
      isAnimated: !!art.videoUrl || !!art.isAnimated
    };
  },

  async logVisit(): Promise<void> {
    try {
      let visitorId = localStorage.getItem('niiko_visitor_id');
      if (!visitorId) {
        visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        localStorage.setItem('niiko_visitor_id', visitorId);
      }
      await fetch(`${SITE_URL}/track_visit.php?vid=${visitorId}`);
    } catch (e) {
      console.warn("Analytics offline.");
    }
  },

  async getAnalytics(): Promise<DailyVisit[]> {
    try {
      const res = await fetch(`${SITE_URL}/visits.json?v=${Date.now()}`);
      if (res.ok) {
        const rawData = await res.json();
        return Object.entries(rawData).map(([date, data]: [string, any]) => ({
          date,
          count: data.unique || data.total || 0,
          uniqueCount: data.unique || 0,
          totalCount: data.total || 0
        })).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
      }
    } catch (e) {}
    return [];
  },

  async getArts(): Promise<{ data: ArtItem[], source: string }> {
    try {
      const remoteArts = await this.getRemoteArts();
      if (remoteArts && remoteArts.length > 0) {
        // Filter out locally deleted arts
        const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
        const filteredArts = remoteArts.filter(a => !deleted.includes(a.id));
        
        const processedArts = filteredArts.map(art => this.processArtItem(art));
        await storage.save('arts', processedArts);
        return { data: processedArts, source: 'hostinger_server' };
      }
      let cached = await storage.load('arts');
      if (cached && cached.length > 0) {
        const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
        const filteredCached = cached.filter((a: any) => !deleted.includes(a.id));
        const processedCached = filteredCached.map((art: ArtItem) => this.processArtItem(art));
        return { data: processedCached, source: 'local_cache' };
      }
      return { data: INITIAL_ARTS.map(art => this.processArtItem(art)), source: 'initial_demos' };
    } catch (e) {
      return { data: INITIAL_ARTS.map(art => this.processArtItem(art)), source: 'error_fallback' };
    }
  },

  async getRemoteArts(): Promise<ArtItem[] | null> {
    try {
      const res = await fetch(`${SITE_URL}/arts.json?v=${Date.now()}`); 
      if (!res.ok) return null;
      const data = await res.json();
      return Array.isArray(data) ? data : null;
    } catch (e) {
      return null;
    }
  },

  async deleteArt(id: string): Promise<boolean> {
    try {
      // Track deleted ID locally
      const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
      if (!deleted.includes(id)) {
        await storage.save('deleted_arts', [...deleted, id], 'registry');
      }

      const res = await fetch(`${SITE_URL}/delete_art.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (res.ok) {
        const cached: ArtItem[] = await storage.load('arts') || [];
        await storage.save('arts', cached.filter(a => a.id !== id));
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },

  async updateArtMetadata(art: ArtItem): Promise<boolean> {
    try {
      const res = await fetch(`${SITE_URL}/update_art.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(art)
      });
      if (res.ok) {
        const cached: ArtItem[] = await storage.load('arts') || [];
        const updated = cached.map(a => a.id === art.id ? art : a);
        await storage.save('arts', updated);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },

  async uploadNewArt(formData: FormData): Promise<{ success: boolean, item?: ArtItem, error?: string }> {
    try {
      const res = await fetch(`${SITE_URL}/upload_art.php`, {
        method: 'POST',
        body: formData
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        if (text.includes('<!DOCTYPE html>') || text.includes('<html')) {
           return { success: false, error: "Arquivo 'upload_art.php' não encontrado no servidor ou sem permissão." };
        }
        return { success: false, error: `O servidor PHP não está respondendo corretamente.` };
      }
      
      if (result.status === 'success' && result.item) {
        const processed = this.processArtItem(result.item);
        const cached: ArtItem[] = await storage.load('arts') || [];
        await storage.save('arts', [processed, ...cached]);
        return { success: true, item: processed };
      }
      return { success: false, error: result.message || 'Erro desconhecido no servidor' };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    }
  },

  async getCategoryCovers(): Promise<Record<string, string>> {
    try {
      const res = await fetch(`${SITE_URL}/category_covers.json?v=${Date.now()}`);
      if (res.ok) {
        const remoteCovers = await res.json();
        const resolved: Record<string, string> = { ...DEFAULT_CATEGORY_COVERS };
        Object.entries(remoteCovers).forEach(([cat, path]) => {
          if (path) {
            resolved[cat] = this.resolveMediaUrl(path as string);
          }
        });
        await storage.save('category_covers', remoteCovers, 'registry');
        return resolved;
      }
    } catch (e) {}
    
    try {
      const cached = await storage.load('category_covers', 'registry');
      if (cached) {
        const resolved: Record<string, string> = { ...DEFAULT_CATEGORY_COVERS };
        Object.entries(cached).forEach(([cat, path]) => {
          if (path) {
            resolved[cat] = this.resolveMediaUrl(path as string);
          }
        });
        return resolved;
      }
    } catch (e) {}
    
    return DEFAULT_CATEGORY_COVERS;
  },

  async saveCategoryCovers(covers: Record<string, string>): Promise<boolean> {
    try {
      const cleanCovers: Record<string, string> = {};
      Object.entries(covers).forEach(([cat, url]) => {
        if (url.includes('/uploads/')) {
          cleanCovers[cat] = url.split('/').pop() || '';
        } else {
          cleanCovers[cat] = url;
        }
      });

      const res = await fetch(`${SITE_URL}/save_category_covers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanCovers)
      });
      
      if (res.ok) {
        await storage.save('category_covers', cleanCovers, 'registry');
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },

  async syncAllCoversToServer(currentCovers: Record<string, string>): Promise<boolean> {
    return this.saveCategoryCovers(currentCovers);
  },

  async getCommissionTiers(): Promise<CommissionTier[]> {
    try {
      const res = await fetch(`${SITE_URL}/commission_tiers.json?v=${Date.now()}`);
      if (res.ok) {
        const remoteTiers = await res.json();
        if (Array.isArray(remoteTiers)) {
          const resolved = remoteTiers.map(t => ({
            ...t,
            demoUrl: this.resolveMediaUrl(t.demoUrl)
          }));
          await storage.save('commission_tiers', remoteTiers, 'registry');
          return resolved;
        }
      }
    } catch (e) {}
    // Fallback to constants
    const { DEFAULT_COMMISSION_TIERS } = await import('../constants');
    return DEFAULT_COMMISSION_TIERS.map(t => ({
      ...t,
      demoUrl: this.resolveMediaUrl(t.demoUrl)
    }));
  },

  async saveCommissionTiers(tiers: CommissionTier[]): Promise<{ success: boolean, error?: string }> {
    try {
      const cleanTiers = tiers.map(t => {
        let cleanUrl = t.demoUrl;
        if (cleanUrl.includes('/uploads/')) {
          cleanUrl = cleanUrl.split('/').pop() || '';
        }
        return { ...t, demoUrl: cleanUrl };
      });

      const res = await fetch(`${SITE_URL}/save_commission_tiers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanTiers)
      });
      
      if (res.ok) {
        const text = await res.text();
        try {
          const result = JSON.parse(text.trim());
          if (result.status === 'success') {
            await storage.save('commission_tiers', cleanTiers, 'registry');
            return { success: true };
          } else {
            return { success: false, error: result.message || 'Erro reportado pelo servidor.' };
          }
        } catch (e) {
          return { success: false, error: `Erro ao processar resposta do servidor.` };
        }
      } else {
        return { success: false, error: `Servidor retornou status ${res.status}` };
      }
    } catch (e) {
      return { success: false, error: (e as Error).message };
    }
  },

  async uploadCommissionTierCover(tierId: string, file: File): Promise<{ success: boolean, url?: string, error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tierId', tierId);
      
      const res = await fetch(`${SITE_URL}/upload_tier_cover.php`, {
        method: 'POST',
        body: formData
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        return { success: false, error: `Erro na resposta do servidor.` };
      }
      
      if (result.status === 'success') {
        return { success: true, url: this.resolveMediaUrl(result.filename) };
      }
      return { success: false, error: result.message || 'Erro de upload' };
    } catch (e) {
      return { success: false, error: "Falha na conexão." };
    }
  },

  async uploadCategoryCover(category: string, file: File): Promise<{ success: boolean, url?: string, error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      
      const res = await fetch(`${SITE_URL}/upload_category_cover.php`, {
        method: 'POST',
        body: formData
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        if (text.includes('<!DOCTYPE html>') || text.includes('<html')) {
          return { success: false, error: "Arquivo 'upload_category_cover.php' não encontrado no servidor ou sem permissão." };
        }
        return { success: false, error: `Resposta inválida do servidor.` };
      }
      
      if (result.status === 'success') {
        return { success: true, url: this.resolveMediaUrl(result.filename) };
      }
      return { success: false, error: result.message || 'Erro de upload desconhecido' };
    } catch (e) {
      return { success: false, error: "Falha na conexão com o servidor Hostinger." };
    }
  },

  async signIn(email: string, pass: string) {
    const users: User[] = await storage.load('users', 'registry') || [];
    const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (!user) return { data: null, error: { message: 'Identidade não encontrada.' } };
    localStorage.setItem('niiko_session', JSON.stringify(user));
    return { data: { user }, error: null };
  },

  async signUp(email: string, pass: string, name: string) {
    const users: User[] = await storage.load('users', 'registry') || [];
    const exists = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (exists) return { data: null, error: { message: 'E-mail já existe.' } };
    const newUser: User = { name, email: email.toLowerCase(), avatarUrl: '', password: pass };
    await storage.save('users', [...users, newUser], 'registry');
    localStorage.setItem('niiko_session', JSON.stringify(newUser));
    return { data: { user: newUser }, error: null };
  },

  async getCurrentUser(): Promise<User | null> {
    const session = localStorage.getItem('niiko_session');
    return session ? JSON.parse(session) : null;
  },

  async signOut() { localStorage.removeItem('niiko_session'); },

  async updateUser(updatedData: Partial<User>) {
    const user = await this.getCurrentUser();
    if (!user) return { data: null, error: 'No session' };
    const updated = { ...user, ...updatedData };
    const users: User[] = await storage.load('users', 'registry') || [];
    const updatedUsers = users.map(u => u.email === user.email ? updated : u);
    await storage.save('users', updatedUsers, 'registry');
    localStorage.setItem('niiko_session', JSON.stringify(updated));
    return { data: updated, error: null };
  },

  async getOrders() { 
    try {
      const res = await fetch(`${SITE_URL}/orders.json?v=${Date.now()}`);
      if (res.ok) {
        let remoteOrders = await res.json();
        if (Array.isArray(remoteOrders)) {
          // Filter out locally deleted orders that might still be in server cache/static file
          const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
          remoteOrders = remoteOrders.filter((o: any) => !deleted.includes(o.id));
          
          await storage.save('orders', remoteOrders);
          return { data: remoteOrders, source: 'hostinger' };
        }
      }
    } catch (e) {}
    const data = await storage.load('orders');
    const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
    const filteredData = Array.isArray(data) ? data.filter((o: any) => !deleted.includes(o.id)) : [];
    return { data: filteredData, source: 'local' }; 
  },

  async saveOrder(order: Order) {
    try {
      await fetch(`${SITE_URL}/save_order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(order)
      });
      const current = await storage.load('orders') || [];
      await storage.save('orders', [order, ...current]);
      return true;
    } catch (e) { return true; }
  },

  async updateOrderStatus(id: string, status: OrderStatus) {
    try {
      await fetch(`${SITE_URL}/update_status.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status })
      });
    } catch (e) {}
    const current = await storage.load('orders') || [];
    const updated = current.map((o: any) => o.id === id ? { ...o, status } : o);
    await storage.save('orders', updated);
    return true;
  },

  async deleteOrder(id: string): Promise<boolean> {
    try {
      // Track deleted ID locally to prevent reappearance from stale server cache
      const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
      if (!deleted.includes(id)) {
        await storage.save('deleted_orders', [...deleted, id], 'registry');
      }

      await fetch(`${SITE_URL}/delete_order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
    } catch (e) {}
    const current = await storage.load('orders') || [];
    const updated = current.filter((o: any) => o.id !== id);
    await storage.save('orders', updated);
    return true;
  },

  async getChatThreads(): Promise<ChatThread[]> {
    try {
      const res = await fetch(`${SITE_URL}/chats.json?v=${Date.now()}`);
      if (res.ok) {
        let data = await res.json();
        if (Array.isArray(data)) {
          await storage.save('chats', data);
          return data;
        }
      }
    } catch (e) {}
    const cached = await storage.load('chats');
    return Array.isArray(cached) ? cached : [];
  },

  async saveChatMessage(userEmail: string, userName: string, message: ChatMessage) {
    try {
      await fetch(`${SITE_URL}/save_chat.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, userName, message })
      });
      const current = await storage.load('chats') || [];
      const updated = [...current];
      const idx = updated.findIndex((t: any) => t.userEmail === userEmail);
      if (idx > -1) {
        updated[idx].messages.push(message);
        updated[idx].lastUpdated = new Date().toISOString();
      } else {
        updated.push({
          userEmail,
          userName,
          messages: [message],
          lastUpdated: new Date().toISOString(),
          unreadCount: message.sender === 'user' ? 1 : 0
        });
      }
      await storage.save('chats', updated);
      return true;
    } catch (e) { return false; }
  },

  async deleteChatThread(userEmail: string): Promise<boolean> {
    try {
      await fetch(`${SITE_URL}/delete_chat.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail })
      });
    } catch (e) {}
    const current = await storage.load('chats') || [];
    const updated = current.filter((t: any) => t.userEmail !== userEmail);
    await storage.save('chats', updated);
    return true;
  },

  async getComments() {
    try {
      const res = await fetch(`${SITE_URL}/comments.json?v=${Date.now()}`);
      if (res.ok) {
        const remoteData = await res.json();
        if (Array.isArray(remoteData)) {
          await storage.save('comments', remoteData);
          return { data: remoteData, error: null };
        }
      }
    } catch (e) {}
    const local = await storage.load('comments') || [];
    return { data: local, error: null };
  },
  async saveComment(comment: Comment) {
    try {
      await fetch(`${SITE_URL}/save_comment.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(comment)
      });
    } catch (e) {}
    const current = await storage.load('comments') || [];
    await storage.save('comments', [comment, ...current]);
    return true;
  },
  async saveAllComments(comments: Comment[]) {
    try {
      await fetch(`${SITE_URL}/save_comments.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(comments)
      });
    } catch (e) {}
    await storage.save('comments', comments);
    return true;
  },

  async getSettings(): Promise<SiteSettings> {
    try {
      const res = await fetch(`${SITE_URL}/settings.json?v=${Date.now()}`);
      if (res.ok) {
        const data = await res.json();
        if (!data.faqs) data.faqs = [];
        await storage.save('settings', data, 'registry');
        return data;
      }
    } catch (e) {}
    const cached = await storage.load('settings', 'registry');
    if (cached) {
      if (!cached.faqs) cached.faqs = [];
      return cached;
    }
    return {
      customRequestIntro_pt: 'Deseja algo que transcenda a galeria comum? Entre em contato com nossos mestres artesãos para solicitar uma arte personalizada para o seu perfil.',
      customRequestIntro_en: 'Looking for something beyond the common gallery? Contact our master craftsmen to request a personalized art for your profile.',
      deviantartUrl: '',
      telegramUrl: '',
      discordUrl: '',
      faqs: []
    };
  },

  async saveSettings(settings: any): Promise<{ success: boolean, message?: string }> {
    try {
      const res = await fetch(`${SITE_URL}/save_settings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (parseError) {
        return { 
          success: false, 
          message: `Erro no servidor: Resposta inválida. Certifique-se de que o arquivo 'save_settings.php' foi enviado corretamente para a Hostinger.` 
        };
      }

      if (res.ok && result.status === 'success') {
        await storage.save('settings', settings, 'registry');
        return { success: true };
      }
      return { success: false, message: result.message || `Erro no servidor: Status ${res.status}` };
    } catch (e) {
      console.error("Erro ao salvar configurações:", e);
      const errorMessage = e instanceof Error ? e.message : String(e);
      return { success: false, message: `Falha na conexão: ${errorMessage}` };
    }
  }
};
